import { useEffect, useRef, useState } from 'react'
import { api, API_URL } from '../../shared/api/http'

type Access = { streamPath?: string; enabled?: boolean }
type Props = { companyId: string; cameraId: string; name: string; speedOnly?: boolean; onDimensions?: (width: number, height: number) => void }

const retryDelays = [250, 500, 1000, 2000, 4000, 8000]
const frameStallMs = 8000
const renderStallMs = 10000

export function CustomLivePlayer({ companyId, cameraId, name, speedOnly = false, onDimensions }: Props) {
  const scope = `${companyId}/${cameraId}/${speedOnly}`
  const [enabled, setEnabled] = useState(false)
  const [frame, setFrame] = useState({ scope: '', url: '' })
  const [state, setState] = useState('Preparando transmissão da IA…')
  const dimensionsRef = useRef(onDimensions)
  const frameLoadedRef = useRef<(url: string) => void>(() => undefined)
  const frameFailedRef = useRef<(url: string) => void>(() => undefined)
  dimensionsRef.current = onDimensions

  useEffect(() => {
    let disposed = false, generation = 0, attempt = 0
    let socket: WebSocket | undefined, request: AbortController | undefined
    let phaseTimer = 0, retryTimer = 0, resumeTimer = 0
    let pendingUrl = '', displayedUrl = '', pendingGeneration = 0
    let firstFrameAt = 0, lastFrameAt = 0, lastRenderedAt = 0, resumeGraceUntil = 0
    const current = (own: number) => !disposed && own === generation
    const canConnect = () => !disposed && !document.hidden && navigator.onLine !== false

    function clearPhaseTimer() {
      window.clearTimeout(phaseTimer)
      phaseTimer = 0
    }

    function stopAttempt() {
      generation++
      clearPhaseTimer()
      request?.abort()
      request = undefined
      const previous = socket
      socket = undefined
      if (previous) {
        previous.onopen = previous.onmessage = previous.onerror = previous.onclose = null
        previous.close()
      }
    }

    function retry(quick = false) {
      if (disposed) return
      stopAttempt()
      window.clearTimeout(retryTimer)
      retryTimer = 0
      if (speedOnly) setFrame({ scope, url: '' })
      if (pendingUrl && pendingUrl !== displayedUrl) {
        URL.revokeObjectURL(pendingUrl)
        pendingUrl = displayedUrl
        pendingGeneration = 0
        setFrame({ scope, url: speedOnly ? '' : displayedUrl })
      }
      setState(navigator.onLine === false ? 'Sem conexão. A transmissão volta automaticamente…' : 'Reconectando à transmissão da IA…')
      if (!canConnect()) return
      const base = quick ? 150 : retryDelays[Math.min(attempt++, retryDelays.length - 1)]
      retryTimer = window.setTimeout(() => {
        retryTimer = 0
        void connect()
      }, Math.min(8000, Math.round(base * (0.8 + Math.random() * 0.4))))
    }

    async function connect() {
      if (!canConnect()) return
      stopAttempt()
      const own = generation
      firstFrameAt = lastFrameAt = lastRenderedAt = 0
      setState(pendingUrl || attempt ? 'Reconectando à transmissão da IA…' : 'Conectando à transmissão da IA…')
      const controller = new AbortController()
      request = controller
      phaseTimer = window.setTimeout(() => { if (current(own)) retry() }, 5000)
      try {
        const access = await api<Access>(`/companies/${companyId}/cameras/${cameraId}/${speedOnly ? 'speed-live' : 'custom-live'}-access`, {
          method: 'POST', signal: controller.signal,
        })
        if (!current(own)) return
        request = undefined
        clearPhaseTimer()
        if (speedOnly && !access.enabled) {
          setEnabled(false); setFrame({ scope, url: '' })
          retryTimer = window.setTimeout(() => void connect(), 15000)
          return
        }
        if (!access.streamPath) throw new Error('live_unavailable')
        setEnabled(true)
        const base = new URL(API_URL)
        const target = new URL(`${base.pathname}${access.streamPath}`, base.origin)
        target.protocol = base.protocol === 'https:' ? 'wss:' : 'ws:'
        const candidate = new WebSocket(target)
        socket = candidate
        candidate.binaryType = 'arraybuffer'
        phaseTimer = window.setTimeout(() => { if (current(own)) retry() }, 6000)
        candidate.onopen = () => {
          if (!current(own)) return
          clearPhaseTimer()
          setState('Aguardando quadros processados…')
          phaseTimer = window.setTimeout(() => { if (current(own)) retry() }, 12000)
        }
        candidate.onmessage = event => {
          if (!current(own) || !(event.data instanceof ArrayBuffer) || event.data.byteLength <= 16) return
          if (speedOnly && ![137,80,78,71,13,10,26,10].every((byte,index)=>new Uint8Array(event.data)[16+index]===byte)) return
          clearPhaseTimer()
          const now = performance.now()
          if (!firstFrameAt) firstFrameAt = now
          lastFrameAt = now
          const url = URL.createObjectURL(new Blob([event.data.slice(16)], { type: speedOnly ? 'image/png' : 'image/jpeg' }))
          if (pendingUrl && pendingUrl !== displayedUrl) URL.revokeObjectURL(pendingUrl)
          pendingUrl = url
          pendingGeneration = own
          setFrame({ scope, url })
        }
        candidate.onerror = candidate.onclose = () => { if (current(own)) retry() }
      } catch {
        if (current(own)) retry()
      }
    }

    function checkFrames() {
      if (!canConnect() || performance.now() < resumeGraceUntil) return
      if (socket?.readyState !== WebSocket.OPEN || !lastFrameAt) return
      const now = performance.now()
      if (speedOnly && now - lastFrameAt >= 750) setFrame({ scope, url: '' })
      if (now - lastFrameAt >= frameStallMs || now - (lastRenderedAt || firstFrameAt) >= renderStallMs) retry()
    }

    function resume() {
      if (!canConnect()) return
      window.clearTimeout(resumeTimer)
      // Give buffered browser events a chance to resume before replacing a healthy socket.
      resumeGraceUntil = performance.now() + 1500
      resumeTimer = window.setTimeout(() => {
        resumeTimer = 0
        if (!canConnect()) return
        resumeGraceUntil = 0
        if (!socket && !request) retry(true)
        else checkFrames()
      }, 1500)
    }

    function offline() {
      retry()
    }

    frameLoadedRef.current = url => {
      if (disposed || url !== pendingUrl) return
      if (displayedUrl && displayedUrl !== url) URL.revokeObjectURL(displayedUrl)
      displayedUrl = url
      lastRenderedAt = performance.now()
      if (pendingGeneration === generation && socket?.readyState === WebSocket.OPEN && lastFrameAt && lastRenderedAt - lastFrameAt < frameStallMs) {
        setState('')
        // A cached first frame must not reset backoff when the camera is still offline.
        if (lastRenderedAt - firstFrameAt >= 10000) attempt = 0
      }
    }
    frameFailedRef.current = url => {
      if (!disposed && pendingGeneration === generation && url === pendingUrl && socket?.readyState === WebSocket.OPEN) retry()
    }
    const watchdog = window.setInterval(checkFrames, speedOnly ? 250 : 1000)
    window.addEventListener('online', resume)
    window.addEventListener('offline', offline)
    window.addEventListener('pageshow', resume)
    document.addEventListener('visibilitychange', resume)
    if (navigator.onLine === false) setState('Sem conexão. A transmissão volta automaticamente…')
    else void connect()

    return () => {
      disposed = true
      stopAttempt()
      window.clearTimeout(retryTimer)
      window.clearTimeout(resumeTimer)
      window.clearInterval(watchdog)
      window.removeEventListener('online', resume)
      window.removeEventListener('offline', offline)
      window.removeEventListener('pageshow', resume)
      document.removeEventListener('visibilitychange', resume)
      frameLoadedRef.current = () => undefined
      frameFailedRef.current = () => undefined
      if (pendingUrl) URL.revokeObjectURL(pendingUrl)
      if (displayedUrl && displayedUrl !== pendingUrl) URL.revokeObjectURL(displayedUrl)
    }
  }, [companyId, cameraId, scope, speedOnly])

  if (speedOnly && !enabled) return null
  return <div className="custom-live-player" aria-label={`${speedOnly ? 'Velocidade ao vivo' : 'Transmissão processada'} de ${name}`} style={speedOnly ? { position: 'absolute', inset: 0, minHeight: 0, background: 'transparent', pointerEvents: 'none', zIndex: 2 } : undefined}>
    {frame.scope === scope && frame.url && <img src={frame.url} alt="" style={speedOnly ? { background: 'transparent' } : undefined} onLoad={event => {
      frameLoadedRef.current(event.currentTarget.currentSrc)
      dimensionsRef.current?.(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight)
    }} onError={event => frameFailedRef.current(event.currentTarget.currentSrc)} />}
    {!speedOnly && state && <span role="status">{state}</span>}
  </div>
}
